/**
 * Frontend env vars — Vite exposes only `VITE_*` to the bundle.
 */

const required = (key: string, fallback?: string): string => {
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  if (value && value.length > 0) return value;
  if (fallback !== undefined) return fallback;
   
  console.error(`[urbania360] Missing required env var: ${key}`);
  return '';
};

/**
 * True when the viewer runs from a bundled offline snapshot (Tauri shell)
 * instead of the live API. Three independent signals so it works across the
 * ways the offline shell can be reached:
 *   - `VITE_U3D_OFFLINE=true` — the build-time flag baked by `build:offline`.
 *   - `?offline=1` — a runtime override useful for testing the offline path
 *     against a normal dev server without a rebuild.
 *   - origin contains "offline" — the Tauri custom scheme (`offline://localhost`
 *     on macOS/Linux, `http://offline.localhost` on Windows/Android/iOS).
 * Guarded for SSR/non-DOM contexts so importing `env` never throws.
 */
function detectOfflineMode(): boolean {
  if (import.meta.env.VITE_U3D_OFFLINE === 'true') return true;
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('offline') === '1') return true;
  } catch {
    // Malformed search string — fall through to the origin check.
  }
  return window.location.origin.includes('offline');
}

export const IS_OFFLINE_MODE = detectOfflineMode();

export const env = {
  SUPABASE_URL: required('VITE_SUPABASE_URL'),
  SUPABASE_ANON_KEY: required('VITE_SUPABASE_ANON_KEY'),
  API_BASE_URL: required('VITE_API_BASE_URL', 'http://localhost:3002'),
  APP_BASE_URL: required('VITE_APP_BASE_URL', 'http://localhost:5174'),
  EMBED_BASE_URL: required('VITE_EMBED_BASE_URL', 'http://localhost:5174'),
  /**
   * Google Maps browser key (optional). Without it the location UI hides
   * itself: no map button for visitors, a short note in the editor.
   */
  GOOGLE_MAPS_API_KEY:
    ((import.meta.env as Record<string, string | undefined>).VITE_GOOGLE_MAPS_API_KEY ?? '').trim(),
  IS_EMBED_MODE: typeof document !== 'undefined' && document.body.dataset.embed === '1',
  IS_OFFLINE_MODE,
};

/**
 * El host ya está mostrando SU spinner encima de este iframe, así que acá no
 * se dibuja ninguno: se veían los dos a la vez (el del showroom, grande y con
 * el logo del proyecto, y el nuestro asomando detrás de su fondo semitransparente).
 *
 * Es el tour dentro del showroom de urbania3d: un iframe de
 * `app.urbania360.app/t/...`, que espera nuestro `urbania360:ready` para sacar
 * el suyo. El embed público (`embed.urbania360.app`, `IS_EMBED_MODE`) va en
 * sitios ajenos que no ponen nada: ahí el spinner propio es lo único que hay.
 */
export const HOST_PAINTS_LOADER =
  !env.IS_EMBED_MODE && typeof window !== 'undefined' && window.parent !== window;
